import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Newspaper, Briefcase, Shirt, Plane, Car, Film, Smartphone, Landmark } from 'lucide-react';

const industries = [
  {
    name: 'Publishers',
    description: 'Unlock higher-value inventory and stronger direct revenue.',
    icon: Newspaper,
    href: '/solutions/publishers',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    name: 'Agencies',
    description: 'Deliver stronger results and more revenue for clients.',
    icon: Briefcase,
    href: '/solutions/agencies',
    gradient: 'from-[#635bff] to-purple-500',
  },
  {
    name: 'Fashion',
    description: 'Turn inspiration into action for fashion consumers.',
    icon: Shirt,
    href: '/solutions/fashion',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    name: 'Travel & Tourism',
    description: 'Drive deeper engagement and more bookings.',
    icon: Plane,
    href: '/solutions/travel-and-tourism',
    gradient: 'from-cyan-500 to-teal-500',
  },
  {
    name: 'Automotive',
    description: 'Let shoppers explore before visiting the showroom.',
    icon: Car,
    href: '/solutions/automotive',
    gradient: 'from-slate-600 to-slate-800',
  },
  {
    name: 'Entertainment & Media',
    description: 'Turn promotions into immersive previews.',
    icon: Film,
    href: '/solutions/entertainment-and-media',
    gradient: 'from-red-500 to-orange-500',
  },
  {
    name: 'Consumer Electronics',
    description: 'Bring product features to life with interactive demos.',
    icon: Smartphone,
    href: '/solutions/consumer-electronics',
    gradient: 'from-violet-500 to-purple-500',
  },
  {
    name: 'Financial Services',
    description: 'Make complex products easier to understand.',
    icon: Landmark,
    href: '/solutions/financial-services',
    gradient: 'from-emerald-500 to-green-500',
  },
];

interface IndustriesSectionProps {
  cmsData?: Record<string, unknown>;
}

const IndustriesSection: React.FC<IndustriesSectionProps> = ({ cmsData }) => {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
        <div className="absolute inset-0 grid-pattern" />
        {/* Subtle gradient orb */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-10 dark:opacity-20">
          <div className="w-full h-full bg-gradient-to-r from-[#635bff] via-[#00d4ff] to-[#ff80b5] rounded-full blur-3xl" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#635bff]/10 dark:bg-[#635bff]/20 border border-[#635bff]/20 text-sm font-semibold text-[#635bff] mb-6">
            Industries
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
            {(cmsData?.headline as string) || 'Interactive ads for'}{' '}
            <span className="stripe-gradient-text">{(cmsData?.headline_gradient as string) || 'every industry'}</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
            {(cmsData?.subtitle as string) || 'From fashion to finance, Adventive powers high-impact campaigns across verticals.'}
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((industry) => (
            <Link
              key={industry.name}
              to={industry.href}
              className="group relative bg-white dark:bg-white/5 rounded-2xl p-6 border border-slate-200 dark:border-white/10 hover:border-[#635bff]/30 dark:hover:border-[#635bff]/30 transition-all duration-300 stripe-card backdrop-blur-sm"
            >
              {/* Icon with gradient */}
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${industry.gradient} mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <industry.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 group-hover:text-[#635bff] transition-colors">
                {industry.name}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                {industry.description}
              </p>
              <div className="flex items-center text-sm font-semibold text-[#635bff] opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                Learn more
                <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
              
              {/* Hover glow */}
              <div className={`absolute -inset-px bg-gradient-to-r ${industry.gradient} rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity duration-300 -z-10`} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
